import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { MapPin, User, GraduationCap } from 'lucide-react';
import { teamMembers } from '../data/teamData';

const OurTeam = () => {
  const [ref, visible] = useScrollReveal();

  return (
    <section
      id="team"
      ref={ref}
      style={{ padding: '160px 24px 100px', background: 'rgba(255,255,255,1)' }}
      data-testid="team-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">Our Team</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            The People Behind<br />
            <span style={{ fontWeight: 300, color: '#64748B' }}>Our Mission</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '600px', margin: '0 auto', lineHeight: 1.7 }}>
            Meet our dedicated team of experts, consultants, and managers working tirelessly to bring holistic psychological guidance and development to every region.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '28px' }}>
          {teamMembers.map((member, i) => (
            <div
              key={member.id}
              className={`card-lift reveal reveal-delay-${(i % 4) + 1} ${visible ? 'visible' : ''}`}
              style={{
                background: member.vacant ? 'rgba(249,249,247,0.4)' : 'rgba(249,249,247,0.8)',
                backdropFilter: 'blur(16px)',
                border: member.vacant ? '1.5px dashed rgba(148,163,184,0.5)' : '1px solid rgba(42,157,143,0.1)',
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: member.vacant ? 'none' : '0 8px 32px rgba(0,0,0,0.03)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ height: member.vacant ? '160px' : '260px', overflow: 'hidden', position: 'relative' }}>
                {member.image ? (
                  <img
                    src={member.image}
                    alt={`${member.name}, ${member.designation}`}
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      objectPosition: 'center 20%',
                      transition: 'transform 0.5s ease',
                    }}
                    onMouseEnter={e => e.target.style.transform = 'scale(1.04)'}
                    onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: member.vacant ? 'transparent' : 'linear-gradient(135deg, rgba(42,157,143,0.1), rgba(42,157,143,0.02))',
                  }}>
                    <User size={64} color={member.vacant ? '#94A3B8' : '#2A9D8F'} strokeWidth={1.2} style={{ opacity: member.vacant ? 0.35 : 0.6 }} />
                  </div>
                )}
                {!member.vacant && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to bottom, transparent 60%, rgba(249,249,247,0.8) 100%)',
                  }} />
                )}

                {/* District Badge */}
                {!member.vacant && member.district ? (
                  <div style={{
                    position: 'absolute', top: '16px', right: '16px',
                    background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)',
                    borderRadius: '99px', padding: '6px 12px',
                    display: 'flex', alignItems: 'center', gap: '6px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  }}>
                    <MapPin size={12} color="#2A9D8F" />
                    <span style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C' }}>
                      {member.district}
                    </span>
                  </div>
                ) : null}
              </div>

              <div style={{ padding: '28px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '19px', color: member.vacant ? '#94A3B8' : '#1A202C', margin: '0 0 6px' }}>
                  {member.name}
                </h3>
                <div style={{
                  fontSize: '14px', color: member.vacant ? '#94A3B8' : '#2A9D8F', fontFamily: 'Manrope',
                  fontWeight: 600, marginBottom: member.vacant ? 0 : '16px'
                }}>
                  {member.designation}
                </div>

                {member.qualification && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '16px' }}>
                    <GraduationCap size={16} color="#94A3B8" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{
                      fontSize: '12px', color: '#475569', fontFamily: 'Inter',
                      lineHeight: 1.5
                    }}>
                      {member.qualification}
                    </span>
                  </div>
                )}

                {member.description && (
                  <p style={{
                    fontSize: '13px', color: '#64748B', lineHeight: 1.6,
                    margin: 'auto 0 0', fontFamily: 'Inter',
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    paddingTop: '16px'
                  }}>
                    {member.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurTeam;
